import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgApexchartsModule } from 'ng-apexcharts';
import { SharedToolsService } from '@huge-monorepo/shared/tools';
import { ActivatedRoute } from '@angular/router';
import { MaterialModule } from '@huge-monorepo/shared/modules';
import { ToolsModule } from '@huge-monorepo/shared/tools';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_LOCALE, MAT_DATE_FORMATS } from '@angular/material/core';
import { forkJoin } from 'rxjs';
import { SharedGenericSingletonService, SharedTeltonikaSingletonService, SharedUsageDataSourceSingletonService } from '@huge-monorepo/shared/services';
import { IHIPTeltonikaService, ICustomerTeltonikaService, TeltonikaDataUsageEntry, ICustomerGenericService, IHIPGenericService } from '@huge-monorepo/shared/interfaces';
import * as moment from 'moment';
import { NavigationService } from '@huge-monorepo/portal/services';
import { Subscription } from 'rxjs';

const SWEDISH_FORMAT = {
    parse: {
        dateInput: 'LL',
    },
    display: {
        dateInput: 'YYYY/MM/DD',
        monthYearLabel: 'MM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MM YYYY',
    },
};
@Component({
    selector: 'huge-monorepo-teltonika-device-data-usage',
    standalone: true,
    imports: [CommonModule, NgApexchartsModule, MaterialModule, ToolsModule],
    templateUrl: './teltonika-device-data-usage.component.html',
    styleUrl: './teltonika-device-data-usage.component.scss',
    providers: [{ provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: SWEDISH_FORMAT },]
})
export class TeltonikaDeviceDataUsageComponent implements OnInit {
    teltonikaService = this.sharedTeltonikaService.getService();
    endDate: Date = new Date();
    startDate: Date = new Date();
    id: number;
    chartOptions: any;
    cumulativeChartOptions: any;
    dataUsage: TeltonikaDataUsageEntry[];
    cumulativeDataUsage: TeltonikaDataUsageEntry[];
    splitDataUsage: TeltonikaDataUsageEntry[];
    chartData: {
        GraphTitle: string;
        SeriesData: { name: string; data: number[]; color: string }[];
        XAxisCategories: string[];
    };
    displaySpinner: boolean = true;
    sidenavObservable: Subscription;
    isSideNavOpen: boolean;

    constructor(
        private tools: SharedToolsService,
        private sharedTeltonikaService: SharedTeltonikaSingletonService<ICustomerTeltonikaService | IHIPTeltonikaService>,
        private activatedRoute: ActivatedRoute,
        private navigationService: NavigationService,
        private usageDataSourceService: SharedUsageDataSourceSingletonService,
        private genericService: SharedGenericSingletonService<ICustomerGenericService | IHIPGenericService>
    ) { }

    ngOnInit(): void {
        this.startDate.setMonth(this.startDate.getMonth() - 1);
        this.activatedRoute.paramMap.subscribe(
            (paraMap: any) => {
                if (paraMap.params.id) {
                    let id = paraMap.params.id;
                    this.id = id;
                    this.initGraph(id);
                }
            }

        );
    }

    async initGraph(id: number): Promise<void> {
        let diffInTime = this.endDate.getTime() - this.startDate.getTime();
        let diffInDays = diffInTime / (1000 * 3600 * 24);
        const monthlyView = diffInDays > 31;
        forkJoin({
            dataUsage: this.genericService.isHIP && this.usageDataSourceService.dataSource == "Radius" ? this.teltonikaService.getRadiusDataUsage(id, this.startDate, this.endDate, monthlyView) : this.teltonikaService.getDataUsage(id, this.startDate, this.endDate, monthlyView),
            splitDataUsage: this.genericService.isHIP && this.usageDataSourceService.dataSource == "Radius" ? this.teltonikaService.getRadiusSplitDataUsage(id, this.startDate, this.endDate, monthlyView) : this.teltonikaService.getSplitDataUsage(id, this.startDate, this.endDate, monthlyView),
        }).subscribe({
            next: ({ dataUsage, splitDataUsage }) => {
                this.dataUsage = dataUsage as TeltonikaDataUsageEntry[];
                this.splitDataUsage = splitDataUsage as TeltonikaDataUsageEntry[];

                this.setChartOptions("Device Data Usage (MB)");
                this.setCumulativeChartOptions("Device Data Usage (MB)");
            },
            error: (error) => {
                console.error(error);
            },
            complete: () => {
                this.displaySpinner = false;
            },
        });
    }

    setChartOptions(title: string) {
        const aggregateData = (data: any[], key: string, filterFn?: (elem: any) => boolean) => {
            const filteredData = filterFn ? data.filter(filterFn) : data;
            return filteredData.reduce((acc: Record<string, number>, elem: any) => {
                const date = this.tools.FormatDateFromDate(new Date(elem.Start));
                acc[date] = (acc[date] || 0) + elem[key];
                return acc;
            }, {});
        };

        const totalUsageData = aggregateData(this.dataUsage, "TotalUsage");

        // Aggregate usage data for MTN (ServiceProviderID: 1)
        const mtnData = aggregateData(
            this.splitDataUsage,
            "TotalUsage",
            (elem) => elem.ServiceProviderID === 1
        );

        // Aggregate usage data for Vodacom (ServiceProviderID: 7 or 8)
        const vodacomData = aggregateData(
            this.splitDataUsage,
            "TotalUsage",
            (elem) => elem.ServiceProviderID === 7 || elem.ServiceProviderID === 8
        );

        const categories = Array.from(
            new Set([...Object.keys(totalUsageData), ...Object.keys(mtnData), ...Object.keys(vodacomData)])
        ).sort();

        const seriesData = {
            total: categories.map((date) => totalUsageData[date] || 0),
            mtn: categories.map((date) => mtnData[date] || 0),
            vodacom: categories.map((date) => vodacomData[date] || 0),
        };

        this.chartOptions = {
            series: [
                {
                    name: "Vodacom Data Usage",
                    data: seriesData.vodacom.map((val) => this.tools.BytesConversion(val, "mb")),
                },
                {
                    name: "MTN Data Usage",
                    data: seriesData.mtn.map((val) => this.tools.BytesConversion(val, "mb")),
                },
                {
                    name: "Total Data Usage",
                    data: seriesData.total.map((val) => this.tools.BytesConversion(val, "mb")),
                },
            ],
            chart: {
                height: 450,
                type: "bar",
                toolbar: {
                    tools: {
                        download: '<div style="width: 150px: display: flex;"><span>Download Graph</span> <i class="material-icons">download</i></div>'
                    }
                },
                events: {
                    mounted: (chart) => {
                        chart.windowResizeHandler();
                    },
                },
            },
            title: {
                text: title,
                align: "left",
            },
            xaxis: {
                categories,
            },
            plotOptions: {
                bar: {
                    columnWidth: "90%",
                    dataLabels: {
                        position: "top",
                    },
                },
            },
            dataLabels: {
                enabled: false,
            },
            colors: ["#FF0000", "#FFD700", "#008000"],
            tooltip: {
                enabled: true,
                y: {
                    formatter: function (val) {
                        return val + " MB";
                    },
                },
            },
        };

        this.sidenavObservable = this.navigationService.GetSideNav((data) => {
            if (this.displaySpinner != true && this.isSideNavOpen != data) {
                this.isSideNavOpen = data;
                let temp = this.displaySpinner;
                this.displaySpinner = true;
                setTimeout(() => {
                    this.displaySpinner = temp;
                });
            }
        });
    }

    setCumulativeChartOptions(title: string) {
        const aggregateData = (data: any[], key: string) => {
            return data.reduce((acc: Record<string, number>, elem: any) => {
                const date = this.tools.FormatDateFromDate(new Date(elem.Start));
                acc[date] = (acc[date] || 0) + elem[key];
                return acc;
            }, {});
        };
        const inBundleData = aggregateData(this.dataUsage, "InBundleAmount");
        const totalUsageData = aggregateData(this.dataUsage, "TotalUsage");
        const categories = Array.from(new Set([...Object.keys(inBundleData), ...Object.keys(totalUsageData)])).sort();

        const cumulativeUsage: (number | null)[] = [];
        const projectedUsage: number[] = [];
        let cumulativeSum = 0;
        let averageDailyUsage = 0;
        let categoriesLength = categories.length;

        // Add future dates if endDate is in the future
        const today = new Date();
        const formattedToday = this.tools.FormatDateFromDate(today);

        if (this.endDate > today) {
            let currentDate = new Date(today.setHours(0, 0, 0, 0));
            while (currentDate <= this.endDate) {
                const formattedDate = this.tools.FormatDateFromDate(currentDate);
                if (!categories.includes(formattedDate)) {
                    categories.push(formattedDate);
                    inBundleData[formattedDate] = 0; // Set usage to zero for future dates
                }
                currentDate.setDate(currentDate.getDate() + 1);
            }
        }

        categories.sort();

        // Compute cumulative usage and calculate average daily usage
        let lastInBundleAmount = 0; // Default to 0 if no non-zero value is found
        for (let i = categories.length - 1; i >= 0; i--) {
            const date = categories[i];
            if (inBundleData[date] !== 0) {
                lastInBundleAmount = inBundleData[date];
                break;
            }
        }

        categories.forEach((date, index) => {
            inBundleData[date] = lastInBundleAmount;
            let usage = 0;
            if (index < Object.keys(totalUsageData).length) {
                usage = totalUsageData[date] || 0;
                cumulativeSum += usage;
                cumulativeUsage.push(this.tools.BytesConversion(cumulativeSum, "mb"));
                averageDailyUsage += usage;
            } else {
                cumulativeUsage.push(null);
            }
        });

        averageDailyUsage /= categoriesLength;
        categories.forEach((_, index) => {
            projectedUsage.push(this.tools.BytesConversion(averageDailyUsage * (index + 1), "mb"));
        });

        const seriesData = {
            inBundle: categories.map((date) => this.tools.BytesConversion(inBundleData[date] || 0, "mb")),
            cumulative: cumulativeUsage,
            projected: projectedUsage,
        };

        this.cumulativeChartOptions = {
            series: [
                {
                    name: "In Bundle Data Usage",
                    data: seriesData.inBundle,
                    color: "#FF0000",
                },
                {
                    name: "Cumulative Data Usage",
                    data: seriesData.cumulative,
                    color: "#008000",
                },
                {
                    name: "Projected Data Usage",
                    data: seriesData.projected,
                    color: "#FFD700",
                },
            ],
            chart: {
                height: 450,
                type: "line", // Change to 'line' for a line chart
                toolbar: {
                    tools: {
                        download: '<div style="width: 150px;display: flex;"><span>Download Graph</span> <i class="material-icons">download</i></div>',
                        selection: false,
                        zoom: false,
                        zoomin: false,
                        zoomout: false,
                        pan: false,
                        reset: false
                    }
                },
                events: {
                    mounted: (chart) => {
                        chart.windowResizeHandler();
                    },
                },
            },
            title: {
                text: title,
                align: "left",
            },
            xaxis: {
                categories, // Use the sorted categories
            },
            tooltip: {
                enabled: true,
                y: {
                    formatter: function (val) {
                        return val + " MB";
                    },
                },
            },
            dataLabels: {
                enabled: false,
            },
            colors: ["#FF0000", "#008000", "#FFD700"],
        };

        this.sidenavObservable = this.navigationService.GetSideNav((data) => {
            if (this.displaySpinner != true && this.isSideNavOpen != data) {
                this.isSideNavOpen = data;
                let temp = this.displaySpinner;
                this.displaySpinner = true;
                setTimeout(() => {
                    this.displaySpinner = temp;
                });
            }
        });
    }

    updateStart(event: any): void {
        this.startDate = event.value.toDate();
    }

    updateEnd(event: any): void {
        this.endDate = event.value.toDate();
    }

    updateGraph(): void {
        this.displaySpinner = true;
        this.initGraph(this.id);
    }

    // async initData(id: number) {
    //     let monthPrior = new Date();
    //     monthPrior.setMonth(monthPrior.getMonth() - 1);
    //     this.teltonikaService.getDataUsage(id, monthPrior, new Date(), false).subscribe({
    //         next: (data) => {
    //             this.dataUsage = data;
    //         },
    //         error: (error) => {
    //             console.error(error);
    //         }

    //     });
    // }

    // initMockData(): void {
    //     this.dataUsage = [
    //         {
    //             "Time": 1684310400,
    //             "Tx": 5712
    //         },
    //         {
    //             "Time": 1684310400,
    //             "Tx": 6283,
    //             "Rx": 2802
    //         },
    //         {
    //             "Time": 1700640000,
    //             "Tx": 420,
    //             "Rx": 420
    //         },
    //         {
    //             "Time": 1700640000,
    //             "Tx": 18110,
    //             "Rx": 2230
    //         },
    //         {
    //             "Time": 1700640000,
    //             "Tx": 3541,
    //             "Rx": 2880
    //         },
    //         {
    //             "Time": 1700643600,
    //             "Tx": 46832,
    //             "Rx": 54949
    //         },
    //         {
    //             "Time": 1700730000,
    //             "Tx": 840,
    //             "Rx": 840
    //         },
    //         {
    //             "Time": 1700730000,
    //             "Tx": 25616,
    //             "Rx": 37696
    //         },
    //         {
    //             "Time": 1701010800,
    //             "Tx": 2490,
    //             "Rx": 3776
    //         },
    //         {
    //             "Time": 1701082800,
    //             "Tx": 911,
    //             "Rx": 420
    //         },
    //         {
    //             "Time": 1701082800,
    //             "Tx": 19815,
    //             "Rx": 23564
    //         },
    //         {
    //             "Time": 1701082800,
    //             "Tx": 4058794,
    //             "Rx": 23328452
    //         },
    //         {
    //             "Time": 1701082800,
    //             "Tx": 13474,
    //             "Rx": 8966
    //         },
    //         {
    //             "Time": 1701082800,
    //             "Tx": 240
    //         },
    //         {
    //             "Time": 1701172800,
    //             "Tx": 3876,
    //             "Rx": 4521
    //         },
    //         {
    //             "Time": 1701172800,
    //             "Tx": 1772
    //         },
    //         {
    //             "Time": 1701183600,
    //             "Tx": 15640,
    //             "Rx": 14258
    //         },
    //         {
    //             "Time": 1701183600,
    //             "Tx": 15023,
    //             "Rx": 12914
    //         },
    //         {
    //             "Time": 1701183600,
    //             "Tx": 27321,
    //             "Rx": 27818
    //         },
    //         {
    //             "Time": 1701183600,
    //             "Tx": 211634,
    //             "Rx": 229681
    //         },
    //         {
    //             "Time": 1704801600,
    //             "Tx": 10672,
    //             "Rx": 9806
    //         },
    //         {
    //             "Time": 1704801600,
    //             "Tx": 66194,
    //             "Rx": 28992
    //         },
    //         {
    //             "Time": 1704801600,
    //             "Tx": 10828,
    //             "Rx": 10142
    //         },
    //         {
    //             "Time": 1704801600,
    //             "Tx": 57423,
    //             "Rx": 32964
    //         },
    //         {
    //             "Time": 1704808800,
    //             "Tx": 158606,
    //             "Rx": 81306
    //         },
    //         {
    //             "Time": 1704808800,
    //             "Tx": 22496,
    //             "Rx": 20620
    //         },
    //         {
    //             "Time": 1704808800,
    //             "Tx": 178740,
    //             "Rx": 169013
    //         },
    //         {
    //             "Time": 1704808800,
    //             "Tx": 126376,
    //             "Rx": 66885
    //         },
    //         {
    //             "Time": 1706014800,
    //             "Tx": 3866417,
    //             "Rx": 2419389
    //         },
    //         {
    //             "Time": 1706522400,
    //             "Tx": 7305,
    //             "Rx": 7810
    //         }
    //     ]
    // }
}

